import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { getHiddenCorporateFolders, updateHiddenCorporateFolders } from '../../../services/user-group';
import './styles.scss';

const ROOT_NAME = 'Forms Available To Me';

function findEntryRoot(tree) {
  return (
    tree.find((f) => f.parent_id === null && f.name === ROOT_NAME) ||
    tree.find((f) => f.parent_id === null && f.static_folder_type === null) ||
    null
  );
}

function buildIndex(tree) {
  const map = new Map();
  const walk = (node, ancestors) => {
    map.set(node.id, { node, ancestors });
    (node.children || []).forEach((child) => walk(child, [...ancestors, node.id]));
  };
  tree.forEach((root) => walk(root, []));
  return map;
}

function collectDescendantIds(node) {
  const ids = [];
  (node.children || []).forEach((child) => {
    ids.push(child.id);
    ids.push(...collectDescendantIds(child));
  });
  return ids;
}

function FolderSettingsModal({ isOpen, closeModal, userGroupId, relationId, onSaved }) {
  const [tree, setTree] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [path, setPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !userGroupId || !relationId) return;
    setLoading(true);
    setError(null);
    getHiddenCorporateFolders(userGroupId, relationId)
      .then((res) => {
        setTree(res.folders_tree || []);
        setSelectedIds(new Set(res.hidden_folder_ids || []));
        setPath([]);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || 'Failed to load folders');
      })
      .finally(() => setLoading(false));
  }, [isOpen, userGroupId, relationId]);

  const idIndex = useMemo(() => buildIndex(tree), [tree]);
  const entryRoot = useMemo(() => findEntryRoot(tree), [tree]);

  const currentFolder = path.length ? path[path.length - 1] : entryRoot;
  const visibleChildren = currentFolder?.children || [];

  const isAncestorHidden = (id) => {
    const entry = idIndex.get(id);
    if (!entry) return false;
    return entry.ancestors.some((aid) => selectedIds.has(aid));
  };

  const toggle = (folder) => {
    if (isAncestorHidden(folder.id)) return;
    const next = new Set(selectedIds);
    if (next.has(folder.id)) {
      next.delete(folder.id);
    } else {
      next.add(folder.id);
      collectDescendantIds(folder).forEach((dId) => next.delete(dId));
    }
    setSelectedIds(next);
  };

  const allVisibleSelected = visibleChildren.length > 0 && visibleChildren.every((c) => selectedIds.has(c.id) || isAncestorHidden(c.id));

  const toggleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allVisibleSelected) {
      visibleChildren.forEach((c) => {
        if (!isAncestorHidden(c.id)) next.delete(c.id);
      });
    } else {
      visibleChildren.forEach((c) => {
        if (!isAncestorHidden(c.id)) {
          next.add(c.id);
          collectDescendantIds(c).forEach((dId) => next.delete(dId));
        }
      });
    }
    setSelectedIds(next);
  };

  const enterFolder = (folder) => {
    if (!folder.children || folder.children.length === 0) return;
    setPath([...path, folder]);
  };

  const goBack = () => {
    setPath(path.slice(0, -1));
  };

  const handleSave = () => {
    setSaving(true);
    setError(null);
    updateHiddenCorporateFolders(userGroupId, relationId, {
      folder_ids: Array.from(selectedIds),
    })
      .then((res) => {
        if (onSaved) onSaved(res);
        closeModal();
      })
      .catch((e) => {
        const data = e?.response?.data;
        if (data?.folder_ids) {
          setError(Array.isArray(data.folder_ids) ? data.folder_ids[0] : data.folder_ids);
        } else if (data?.detail) {
          setError(data.detail);
        } else {
          setError('Failed to save');
        }
      })
      .finally(() => setSaving(false));
  };

  const handleClose = () => {
    if (saving) return;
    closeModal();
  };

  return (
    <Modal
      className="modal-default folder-settings-modal"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Select folders to hide"
      ariaHideApp={false}
    >
      <h2 className="modal-header">
        Select folders to hide
        <button className="btn btn-icon" onClick={handleClose}>
          <i className="icon-icon-x"></i>
        </button>
      </h2>

      <div className="folder-settings-body">
        {path.length > 0 && (
          <button type="button" className="folder-back" onClick={goBack}>
            <i className="icon-icon-chevron-left mr-2"></i>
            <span>{currentFolder?.name}</span>
          </button>
        )}

        {loading && <div className="folder-list-state">Loading...</div>}

        {!loading && visibleChildren.length > 0 && (
          <label className="folder-row select-all-row">
            <label className="form-control-checkbox forms-modal-folder-checkbox" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
              <span>Select All</span>
            </label>
          </label>
        )}

        {!loading && (
          <div className="folder-list">
            {visibleChildren.map((folder) => {
              const ancestorHidden = isAncestorHidden(folder.id);
              const checked = selectedIds.has(folder.id) || ancestorHidden;
              const hasChildren = folder.children && folder.children.length > 0;
              return (
                <div className={`folder-row${hasChildren ? ' folder-row-clickable' : ''}`} key={folder.id}>
                  <label
                    className="form-control-checkbox forms-modal-folder-checkbox"
                    style={{ marginBottom: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggle(folder)} />
                    <span></span>
                  </label>
                  <button type="button" className="folder-row-name" onClick={() => enterFolder(folder)} disabled={!hasChildren}>
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="2" width="32" height="32" rx="16" fill="#DBF0FF" />
                      <rect x="2" y="2" width="32" height="32" rx="16" stroke="#F0FBFF" strokeWidth="4" />
                      <path
                        d="M24.6673 22.6667C24.6673 23.0203 24.5268 23.3594 24.2768 23.6095C24.0267 23.8595 23.6876 24 23.334 24H12.6673C12.3137 24 11.9746 23.8595 11.7245 23.6095C11.4745 23.3594 11.334 23.0203 11.334 22.6667V13.3333C11.334 12.9797 11.4745 12.6406 11.7245 12.3905C11.9746 12.1405 12.3137 12 12.6673 12H16.0007L17.334 14H23.334C23.6876 14 24.0267 14.1405 24.2768 14.3905C24.5268 14.6406 24.6673 14.9797 24.6673 15.3333V22.6667Z"
                        stroke="#3298E1"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    <span>{folder.name}</span>
                    {hasChildren && <i className="icon-icon-chevron-right ml-auto"></i>}
                  </button>
                </div>
              );
            })}
            {visibleChildren.length === 0 && <div className="folder-list-state">No folders</div>}
          </div>
        )}

        {error && <div className="form-error mt-2">{error}</div>}
      </div>

      <div className="modal-footer">
        <button className="btn btn-outline" onClick={handleClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn" onClick={handleSave} disabled={saving || loading}>
          Hide
        </button>
      </div>
    </Modal>
  );
}

export default FolderSettingsModal;
